import { Box } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFilterParams } from '@/shared/components/ui/data-table/hooks/use-filter-params/use-filter-params';
import { TQuerySearchbarProps } from '@/shared/components/ui/query-searchbar/query-searchbar.types';
import { Searchbar } from '@/shared/components/ui/searchbar/searchbar';
import { useDebounce } from '@/shared/hooks/use-debounce/use-debounce';

export const QuerySearchbar = <T,>({
  placeholder,
  width = '20rem',
  queryParam,
  disabled,
  ...rest
}: TQuerySearchbarProps<T>): React.ReactElement => {
  const { t } = useTranslation();
  const { searchParam, setSearchParam, removeParameter } = useFilterParams({
    queryParam,
  });
  const [value, setValue] = useState(searchParam);
  const [prevSearchParam, setPrevSearchParam] = useState(searchParam);

  // This resets the searchbar in case the searchParam is reset externally ("reset filters")
  if (searchParam !== prevSearchParam) {
    setPrevSearchParam(searchParam);
    setValue(searchParam);
  }

  useDebounce({
    value: value,
    afterDebounce: (value) => {
      if (!value) {
        removeParameter();
      } else {
        setSearchParam(value);
      }
    },
  });

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  if (disabled) {
    return <Box />;
  }

  return (
    <Searchbar
      {...rest}
      width={width}
      onClearSearchbar={() => setValue('')}
      placeholder={placeholder ?? t('common:search')}
      value={value}
      onSearchbarChange={handleSearchChange}
    />
  );
};
